import { useState } from "react";
import { Globe02Icon } from "@hugeicons-pro/core-bulk-rounded";

import { PageHeader } from "@/components/layout/page-header";
import { GlobalFilterBar } from "@/components/filters/global-filter-bar";
import { ExportMenu } from "@/components/dashboard/export-menu";
import type { Column } from "@/components/dashboard/breakdown-table";
import { ReachFigure } from "@/components/dashboard/reach-figure";
import { FadeIn } from "@/components/ui/motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RegionFlag } from "@/components/ui/region-flag";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { useBreakdown } from "@/lib/use-metrics";
import { useExactReach, reachCaveats, type ReachValue } from "@/lib/use-reach";
import { useBenchmarkEditor } from "@/lib/use-benchmark-editor";
import { EditableTarget } from "@/components/dashboard/editable-target";
import { useFilters } from "@/lib/filters";
import { useReference } from "@/lib/use-reference";
import { money, metricMoney } from "@/lib/use-money";
import { MARKET_CURRENCY } from "@/lib/currency";
import { formatMetric, percent } from "@/lib/format";
import { scaleTarget, onTrack, benchmarkTarget } from "@/lib/benchmarks";
import { cn } from "@/lib/utils";
import type { BreakdownRow } from "@/lib/api/metrics";
import type { Region, Benchmark, ActivityType, ActivityFilter, MetricDef } from "@/lib/api/types";

// The KPIs per objective (doelstelling), in order. E-commerce KPIs use the same
// blended basis as the E-commerce page — every campaign in the market — so a
// market reads the same here as there. Awareness reach and ER come from each
// platform's own period reach; job KPIs from Teamtailor.
const MARKET_KPIS: { key: string; group: ActivityType }[] = [
  { key: "roas", group: "ecom" },
  { key: "orders", group: "ecom" },
  { key: "conversion_rate", group: "ecom" },
  { key: "aov", group: "ecom" },
  { key: "cpa", group: "ecom" },
  { key: "reached_accounts", group: "awareness" },
  { key: "organic_engagement_rate", group: "awareness" },
  { key: "applications", group: "job_ads" },
  { key: "cost_per_application", group: "job_ads" },
];
const AWARENESS_KEYS = new Set(["reached_accounts", "organic_engagement_rate"]);

const MARKET_NAME: Record<string, string> = { EU: "Europe", US: "United States", CA: "Canada" };
const ORDER: Region[] = ["EU", "US", "CA"];

function activityMatches(group: ActivityType, activity: ActivityFilter): boolean {
  switch (activity) {
    case "ecom": return group === "ecom";
    case "awareness": return group === "awareness";
    case "job_ads": return group === "job_ads";
    case "non_ecom": return group !== "ecom";
    default: return true;
  }
}

/** Market rates can be tiny (EU: 4 paid orders on 113K clicks): two decimals,
 *  and two significant digits below 0.1%, so a real rate never reads as 0.0%. */
function formatKpi(value: number | null, def: MetricDef, fmt: (v: number | null | undefined) => string): string {
  if (def.unit !== "percent" || value == null) return formatMetric(value, def.unit, fmt);
  return value !== 0 && Math.abs(value) < 0.001 ? `${(value * 100).toPrecision(2)}%` : percent(value, 2);
}

function benchmarkFor(benchmarks: Benchmark[], key: string, region: Region, days: number): number | null {
  const raw = benchmarkTarget(benchmarks, key, region);
  return raw == null ? null : scaleTarget(raw, key, "monthly", days);
}

type MarketExportRow = { market: string; kpi: string; actual: string; benchmark: string; status: string };
const EXPORT_COLUMNS: Column<MarketExportRow>[] = [
  { key: "market", header: "Markt", render: (r) => r.market, exportValue: (r) => r.market },
  { key: "kpi", header: "KPI", render: (r) => r.kpi, exportValue: (r) => r.kpi },
  { key: "actual", header: "Actueel", render: (r) => r.actual, exportValue: (r) => r.actual },
  { key: "benchmark", header: "Benchmark", render: (r) => r.benchmark, exportValue: (r) => r.benchmark },
  { key: "status", header: "Status", render: (r) => r.status, exportValue: (r) => r.status },
];

export function Markten() {
  const f = useFilters();
  const { metrics, benchmarks, fx } = useReference();
  const { saveByKey } = useBenchmarkEditor();
  // Every market side by side: the market filter of other pages must not apply here.
  const ecom = useBreakdown("region", "all", "local", "all");
  const awareness = useBreakdown("region", "awareness", "local", "all");
  const jobs = useBreakdown("region", "job_ads", "local", "all");
  const reach = useExactReach({ activity: "awareness", allMarkets: true });
  const [picked, setPicked] = useState<Region | null>(null);

  const loading = ecom.isPending || awareness.isPending || jobs.isPending;
  const index = (rows: BreakdownRow[] | undefined) => new Map((rows ?? []).map((r) => [r.key, r]));
  const ecomIdx = index(ecom.data);
  const awareIdx = index(awareness.data);
  const jobsIdx = index(jobs.data);

  const kpis = MARKET_KPIS.filter((k) => activityMatches(k.group, f.activity));
  const present = ORDER.filter((r) => ecomIdx.has(r) || awareIdx.has(r) || jobsIdx.has(r));
  const active: Region | undefined = picked && present.includes(picked) ? picked : present[0];

  const reachOf = (region: Region): ReachValue | undefined => reach.get("region", region);
  const actualOf = (key: string, group: ActivityType, region: Region): number | null => {
    if (AWARENESS_KEYS.has(key)) {
      const v = reachOf(region);
      return (key === "reached_accounts" ? v?.reach : v?.er) ?? null;
    }
    const row = (group === "job_ads" ? jobsIdx : ecomIdx).get(region);
    return row ? ((row as unknown as Record<string, number | null>)[key] ?? null) : null;
  };
  const targetOf = (def: MetricDef, region: Region) => {
    const code = MARKET_CURRENCY[region] ?? "EUR";
    const target = benchmarkFor(benchmarks, def.key, region, f.windowDays);
    const comparable = target != null && def.unit === "currency" ? (fx[code] ? target / fx[code]! : null) : target;
    return { target, comparable };
  };

  const exportRows: MarketExportRow[] = present.flatMap((region) => {
    const code = MARKET_CURRENCY[region] ?? "EUR";
    const m = metricMoney(code);
    const targetMoney = money(code, fx);
    return kpis.flatMap(({ key, group }) => {
      const def = metrics.find((d) => d.key === key);
      if (!def) return [];
      const actual = actualOf(key, group, region);
      const { target, comparable } = targetOf(def, region);
      const ok = onTrack(actual, comparable, def.higher_is_better);
      const caveats = key === "reached_accounts" ? reachCaveats(reachOf(region)) : [];
      return [{
        market: `${MARKET_NAME[region] ?? region} (${code})`,
        kpi: def.label,
        actual: AWARENESS_KEYS.has(key) && reach.loading ? "…" : formatKpi(actual, def, m.fmt) + (caveats.length ? ` (${caveats.join(" ")})` : ""),
        benchmark: target != null ? formatMetric(target, def.unit, targetMoney.fmt) : "",
        status: ok == null ? "n.v.t." : ok ? "Op koers" : "Onder benchmark",
      }];
    });
  });

  const code = active ? (MARKET_CURRENCY[active] ?? "EUR") : "EUR";
  const m = metricMoney(code);
  const targetMoney = money(code, fx);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Markten"
        icon={Globe02Icon}
        title="Per markt"
        description="Voor elke markt de KPI's per doelstelling naast hun benchmark, in de lokale valuta (EUR/USD/CAD)."
        tabs={
          <div className="flex w-full flex-wrap items-center gap-2">
            <GlobalFilterBar showCurrency={false} showRegion={false} />
            {!loading && present.length > 0 && (
              <div className="ml-auto">
                <ExportMenu name="Prado Pulse — Per markt" columns={EXPORT_COLUMNS} rows={exportRows} />
              </div>
            )}
          </div>
        }
      />

      {loading ? (
        <Skeleton className="h-[520px] rounded-2xl" />
      ) : !active ? (
        <EmptyState
          icon={Globe02Icon}
          title="Geen data voor deze selectie"
          description="Kies een andere periode of filter om gegevens te zien."
        />
      ) : (
        <FadeIn delay={0.05}>
          <Card className="overflow-hidden p-0">
            {/* Section header — market filter tabs (flag icons, no emoji) */}
            <div className="band-header flex flex-wrap items-center gap-1.5 !border-b border-border p-2">
              {present.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setPicked(r)}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    active === r ? "bg-card text-foreground shadow-sm ring-1 ring-border" : "text-muted-foreground hover:bg-card/60",
                  )}
                >
                  <RegionFlag region={r} size={22} />
                  {MARKET_NAME[r] ?? r}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2 pr-1">
                <span className="text-xs text-muted-foreground">Lokale valuta</span>
                <Badge variant="outline" size="sm">{code}</Badge>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>KPI</TableHead>
                  <TableHead className="text-right">Actueel</TableHead>
                  <TableHead className="text-right">Benchmark</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {kpis.map(({ key, group }, i) => {
                  const def = metrics.find((d) => d.key === key);
                  if (!def) return null;
                  const actual = actualOf(key, group, active);
                  const { target, comparable } = targetOf(def, active);
                  const rawTarget = benchmarkTarget(benchmarks, key, active);
                  const ok = onTrack(actual, comparable, def.higher_is_better);
                  const newGroup = i > 0 && kpis[i - 1].group !== group;
                  return (
                    <TableRow key={key} className={cn(newGroup && "border-t-2")}>
                      <TableCell className="font-medium">
                        {def.label}
                        {(i === 0 || newGroup) && (
                          <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                            {group === "ecom" ? "E-commerce" : group === "awareness" ? "Awareness" : "Job ads"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {AWARENESS_KEYS.has(key)
                          ? <ReachFigure loading={reach.loading} value={reachOf(active)} format={() => formatKpi(actual, def, m.fmt)} reachCaveatsApply={key === "reached_accounts"} />
                          : formatKpi(actual, def, m.fmt)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <EditableTarget
                          value={rawTarget}
                          display={target != null ? formatMetric(target, def.unit, targetMoney.fmt) : "—"}
                          unit={def.unit}
                          money={targetMoney.fmt}
                          onSave={(v) => saveByKey({ metric_key: key, activity_type: group, region: active, brand_id: null }, v)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={ok == null ? "outline" : ok ? "success" : "warning"} size="sm">
                          {ok == null ? "—" : ok ? "Op koers" : "Onder doel"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="space-y-1 border-t border-border px-5 py-3 text-xs text-muted-foreground">
              {kpis.some((k) => k.group === "ecom") && (
                <p>
                  E-commerce: alle campagnes in de markt samen, zoals op de E-commercepagina. ROAS, CVR en CPA rekenen met
                  betaalde kanalen (ROAS = betaalde omzet ÷ betaalde uitgaven, CVR = betaalde orders ÷ betaalde klikken,
                  CPA = betaalde uitgaven ÷ betaalde orders); AOV = omzet ÷ orders.
                  {active === "US" && " Webshopomzet en orders omvatten de volledige prado.com-shop, inclusief Canada."}
                  {active === "CA" && " Omzet en orders zijn kanaaltoegeschreven resultaten, geen afzonderlijk Canadees webshoptotaal."}
                </p>
              )}
              {kpis.some((k) => k.group === "awareness") && (
                <p>Awareness: bereik zoals elk platform het voor de hele periode telt, opgeteld over de platformen van de markt (Google en Bing: impressies); ER = engagement ÷ impressies, over de platformen die engagement leveren.</p>
              )}
            </div>
          </Card>
        </FadeIn>
      )}
    </div>
  );
}
